import { Calendar, ChevronDown, Eye, FileText, RotateCcw, Search, SearchX } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import Select from "@/components/ui/Select";
import { TableSkeleton } from "@/components/ui/Skeleton";
import type { TopUpRequest } from "@/types/wallet";
import { formatDate, formatINR, formatTime, RANGES, TOPUP_STATUS_META } from "@/pages/retailer/wallet/data";
import { TOPUP_STATUS_FILTERS } from "../data";
import ReceiptPreviewModal from "./ReceiptPreviewModal";
import { usePermission } from "@/context/PermissionContext";

interface TopupRequestsTabProps {
  loading: boolean;
  search: string;
  onSearch: (v: string) => void;
  status: string;
  onStatus: (v: string) => void;
  retailer: string;
  onRetailer: (v: string) => void;
  range: string;
  onRange: (v: string) => void;
  rangeOpen: boolean;
  onRangeOpen: (v: boolean) => void;
  onReset: () => void;
  rows: TopUpRequest[];
  retailerOptions: { value: string; label: string }[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange: (n: number) => void;
  onReview: (request: TopUpRequest) => void;
  onViewDetail: (request: TopUpRequest) => void;
}

export default function TopupRequestsTab(props: TopupRequestsTabProps) {
  const { loading, search, onSearch, status, onStatus, retailer, onRetailer, range, onRange, rangeOpen, onRangeOpen, onReset, rows, retailerOptions, page, pageSize, total, onPageChange, onPageSizeChange, onReview, onViewDetail } = props;
  const [previewReceiptRequest, setPreviewReceiptRequest] = useState<TopUpRequest | null>(null);
  const { canApprove } = usePermission();

  const columns: Column<TopUpRequest>[] = [
    {
      key: "requestId",
      header: "Request ID",
      render: (row) => <span className="font-mono text-[13px] text-ink">{row.requestId}</span>,
    },
    {
      key: "retailerId",
      header: "Retailer",
      render: (row) => {

        return (
          <div>
            <p className="text-sm font-medium text-ink">{row.shopName || row.retailerName || "Retailer"}</p>
            <p className="text-xs text-ink-secondary">{row.userCode || row.mobile || (!row.retailerId?.includes("-") ? row.retailerId : "")}</p>
          </div>
        );
      },
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => <span className="whitespace-nowrap text-sm font-semibold text-ink">{formatINR(row.amount)}</span>,
    },
    {
      key: "paymentMode",
      header: "Payment Mode",
      render: (row) => <span className="text-sm text-ink">{row.paymentMode}</span>,
    },
    {
      key: "referenceNumber",
      header: "Reference Number",
      render: (row) => <span className="font-mono text-[13px] text-ink-secondary">{row.referenceNumber}</span>,
    },
    {
      key: "receipt",
      header: "Receipt",
      render: (row) =>
        row.receiptFileName ? (
          <button
            type="button"
            onClick={() => setPreviewReceiptRequest(row)}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
            title="View receipt"
          >
            <FileText className="h-3.5 w-3.5" />
            <span className="max-w-[100px] truncate">{row.receiptFileName}</span>
          </button>
        ) : (
          <span className="text-xs text-ink-muted">—</span>
        ),
    },
    {
      key: "requestedAt",
      header: "Requested On",
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{formatDate(row.requestedAt)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.requestedAt)}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const meta = TOPUP_STATUS_META[row.status];
        return (
          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.bg} ${meta.text}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {meta.label}
          </span>
        );
      },
    },
    {
      key: "action",
      header: "Actions",
      align: "right",
      render: (row) => {
        if (row.status === "PENDING") {
          return canApprove("wallets") ? (
            <Button size="sm" className="h-8" onClick={() => onReview(row)}>
              Review
            </Button>
          ) : (
            <button
              type="button"
              onClick={() => onViewDetail(row)}
              className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink cursor-pointer"
            >
              <Eye className="h-3.5 w-3.5" /> View
            </button>
          );
        }
        return (
          <button
            type="button"
            onClick={() => onViewDetail(row)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-primary-500 cursor-pointer"
            aria-label="View top-up details"
          >
            <Eye className="h-4 w-4" />
          </button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-[10px] border border-line bg-card p-4 shadow-card lg:flex-row lg:items-center">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
              <Search className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by request ID, retailer or reference number"
              className="h-[42px] w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500/30"
            />
          </div>
          <div className="sm:w-44">
            <Select value={status} onChange={(e) => onStatus(e.target.value)} options={TOPUP_STATUS_FILTERS} />
          </div>
          <div className="sm:w-52">
            <Select value={retailer} onChange={(e) => onRetailer(e.target.value)} options={[{ value: "", label: "All Retailers" }, ...retailerOptions]} />
          </div>
          <div className="relative sm:w-44">
            <button
              type="button"
              onClick={() => onRangeOpen(!rangeOpen)}
              className="flex h-[42px] w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-ink hover:bg-slate-50 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-ink-muted" />
                {range}
              </span>
              <ChevronDown className="h-4 w-4 text-ink-muted" />
            </button>
            {rangeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => onRangeOpen(false)} />
                <div className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        onRange(r);
                        onRangeOpen(false);
                      }}
                      className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                        range === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
          Reset
        </Button>
      </div>
      {loading ? (
        <TableSkeleton rows={5} columns={8} />
      ) : rows.length === 0 ? (
        <EmptyState icon={SearchX} title="No top-up requests" description="Try adjusting your filters or date range." actionLabel="Reset Filters" onAction={onReset} />
      ) : (
        <DataTable columns={columns} data={rows} rowKey={(r) => r.id} pageSize={10} pageSizeOptions={[10, 25, 50]} />
      )}

      <ReceiptPreviewModal
        open={!!previewReceiptRequest}
        onClose={() => setPreviewReceiptRequest(null)}
        request={previewReceiptRequest}
      />
    </div>
  );
}